import React, { useState, useEffect } from 'react';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { format, parseISO } from 'date-fns';
import request from '@nineeng/request';

/*
{
	type: 'UserApproval',
	roleIds: [''],
}

3 states:
- loading (getting permissions)
- open for approval (user either can or cannot approve)
- approved (by user, timestamp)
*/

const UserApproval = (props) => {
	const {
		name,
		value,
		onChange,
		label,
		roleIds,
		disabled,
		size = 'medium',
		userUrl = '/api/auth/current-user',
	} = props;
	
	const isSmall = size === 'small';
	const [isLoading, setLoading] = useState(true);
	const [user, setUser] = useState();

	const handleApproval = () => {
		if(!user) return;
		const approval = {
			timestamp: new Date(),
			userId: user.id,
			fullName: user.fullName,
		}; 
		onChange({ target: { name, value: approval } });
	};

	useEffect(() => {
		const getUser = async () => {
			try {
				// Fetch authenticated user
				const { body: currentUser } = await request.get(userUrl);

				// Check roles (if necessary)
				let canApprove = true;
				if(roleIds) {
					canApprove = !!currentUser.roles.find(r => roleIds.includes(r.id));
				}
				if(canApprove) setUser(currentUser);
			} catch (e) {
				console.warn(e);
			}
			setLoading(false);
		};

		getUser();
	}, []);

	const formatTimestamp = () => {
		if(!value.timestamp) return null;
		if(value.timestamp instanceof Date) return format(value.timestamp, 'dd/MM/yyyy HH:mm');
		format(parseISO(value.timestamp), 'dd/MM/yyyy HH:mm');
	};

	const canApprove = !disabled && !!user;
	const boxStyle = { textAlign: 'right', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'end', flexGrow: 1 };

	return (
		<Paper 
			variant='outlined' 
			sx={{ 
				borderColor: value && 'success.main',
				position: 'relative', 
				display: 'flex',
				flexDirection: 'row',
				flexWrap: 'wrap',
				padding: isSmall ? 1 : 2, 
				overflow: 'hidden', 
				maxWidth: 300,
				minHeight: 110,
				marginBottom: 2,
			}}
		>
			{label && <Typography variant='caption' sx={{ width: '100%' }}>{label}</Typography>}
			<Box sx={{ width: 50, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
				{value ? <CheckCircleIcon sx={{ fontSize: 50 }} color='success' /> : <HourglassEmptyIcon sx={{ fontSize: 50, opacity: 0.3 }} />}
			</Box>
			{value && <Box sx={boxStyle}>
				<Typography variant='overline'>Approved by</Typography>
				<Typography variant='subtitle2'>{value.fullName}</Typography>
				<Typography variant='subtitle2'>{formatTimestamp()}</Typography>
			</Box>}

			{!value && <Box sx={boxStyle}>
				<Typography variant='overline'>Approval needed</Typography>
				{canApprove 
					? <Button variant='contained' color='secondary'  onClick={handleApproval}>Approve</Button>
					: <Tooltip title="You are not allowed to approve" placement='left'>
						<Box>
							<Button variant='outlined' color='secondary' disabled>Approve</Button>
						</Box>
					</Tooltip>
				}
			</Box>}
			
			<Backdrop sx={{ position: 'absolute' }} open={isLoading}><CircularProgress /></Backdrop>
		</Paper>
	);
};

UserApproval.type = 'UserApproval';

export default UserApproval;